import { ProfileFields } from "@/components/profile/ProfileFields";
/**
 * Sprint S13.1.A — Step 1: Welcome (first_name + DOB + sex_biological).
 *
 * Uses react-hook-form + zod resolver. On valid submit advances state +
 * goes к next step. defaultValues hydrated from store (resume support).
 */
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useOnboardingStore } from "@/stores/onboardingStore";


const TODAY_ISO = new Date().toISOString().split("T")[0];


export function Step1Welcome() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setData = useOnboardingStore((s) => s.setData);
  const nextStep = useOnboardingStore((s) => s.nextStep);

  const schema = z.object({
    first_name: z
      .string()
      .min(1, t("onboarding.step.welcome.first_name_required"))
      .max(100),
    date_of_birth: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, t("onboarding.step.welcome.dob_invalid"))
      .refine((v) => v <= TODAY_ISO, {
        message: t("onboarding.step.welcome.dob_in_future"),
      }),
    sex_biological: z.enum(["male", "female", "prefer_not_to_say"]),
  });

  type WelcomeForm = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WelcomeForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: data.first_name ?? "",
      date_of_birth: data.date_of_birth ?? "",
      sex_biological: data.sex_biological,
    },
  });

  const onSubmit = (values: WelcomeForm) => {
    setData(values);
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.welcome.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.welcome.subtitle")}
        </p>
      </div>

      <Input
        label={t("onboarding.step.welcome.first_name_label")}
        placeholder={t("onboarding.step.welcome.first_name_placeholder")}
        hint={t("onboarding.step.welcome.first_name_hint")}
        error={errors.first_name?.message}
        {...register("first_name")}
      />

      <Input
        type="date"
        label={t("onboarding.step.welcome.dob_label")}
        max={TODAY_ISO}
        error={errors.date_of_birth?.message}
        {...register("date_of_birth")}
      />

      <label className="block">
        <span className="mb-1.5 block text-[13px] font-medium text-navy">
          {t("onboarding.step.welcome.sex_label")}
        </span>
        <select
          {...register("sex_biological")}
          className="w-full rounded-lg border border-cream-border bg-cream-light px-4 py-3 text-[15px] text-navy-deep focus:border-amber/40 focus:outline-none focus:ring-2 focus:ring-amber/60"
        >
          <option value="prefer_not_to_say">
            {t("onboarding.sex.prefer_not_to_say")}
          </option>
          <option value="male">{t("onboarding.sex.male")}</option>
          <option value="female">{t("onboarding.sex.female")}</option>
        </select>
      </label>

      <ProfileFields section="body" data={data} onChange={setData} />

      <div className="pt-2">
        <Button type="submit" data-onboarding-next className="w-full sm:w-auto">
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </form>
  );
}
